import React, { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { Lead, LeadStats } from '../types/Lead';
import { loadLeads, calculateStats } from '../services/leadService';
import { firestoreService } from '../services/firebaseFirestore';
import { useAuth } from './AuthContext';

interface LeadsContextType {
  leads: Lead[];
  stats: LeadStats;
  loading: boolean;
  error: string | null;
  updateLeadInCache: (updatedLead: Lead) => void;
}

const EMPTY_STATS: LeadStats = {
  totalLeads: 0,
  topLeads: 0,
  companySizes: [],
  sectors: [],
  geographies: [],
};

const LeadsContext = createContext<LeadsContextType | undefined>(undefined);

export const useLeads = (): LeadsContextType => {
  const context = useContext(LeadsContext);
  if (context === undefined) {
    throw new Error('useLeads must be used within a LeadsProvider');
  }
  return context;
};

interface LeadsProviderProps {
  children: ReactNode;
}

export const LeadsProvider: React.FC<LeadsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [stats, setStats] = useState<LeadStats>(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Track whether the initial load has completed so we don't show the
  // loading spinner again when Dashboard remounts after a tab switch.
  const initialLoadDone = useRef(false);

  // One-time initial fetch — gives us data before the subscription fires.
  useEffect(() => {
    if (initialLoadDone.current) return;

    loadLeads()
      .then((loadedLeads) => {
        setLeads(loadedLeads);
        setStats(calculateStats(loadedLeads));
        initialLoadDone.current = true;
      })
      .catch((err) => {
        console.error('LeadsContext: initial fetch failed', err);
        setError('Failed to load leads data');
      })
      .finally(() => setLoading(false));
  }, []);

  // Real-time subscription — starts when the user logs in, stays alive
  // for the lifetime of the provider (across route changes).
  useEffect(() => {
    if (!user) {
      setLeads([]);
      setStats(EMPTY_STATS);
      initialLoadDone.current = false;
      return;
    }

    const unsubscribe = firestoreService.leads.subscribeToAll((newLeads: Lead[]) => {
      setLeads(newLeads);
      setStats(calculateStats(newLeads));
      setLoading(false);
      initialLoadDone.current = true;
    });

    return unsubscribe;
  }, [user]);

  const updateLeadInCache = useCallback((updatedLead: Lead) => {
    setLeads((prev) => prev.map((l) => (l.id === updatedLead.id ? updatedLead : l)));
  }, []);

  return (
    <LeadsContext.Provider value={{ leads, stats, loading, error, updateLeadInCache }}>
      {children}
    </LeadsContext.Provider>
  );
};
